import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Loader2, Mail, Phone, MapPin, Send } from "lucide-react";
import { apiFetch, formatDetail, postReply, ReplyBox, ReplyHistory } from "./bits";

const FULFILMENT_LABELS = {
  own: "Own delivery riders",
  aflimart: "Aflimart delivery",
  both: "Own riders + Aflimart delivery",
  pickup: "Pickup only",
};

const STATUS_LABELS = { pending: "PENDING REVIEW", approved: "APPROVED", declined: "DECLINED" };
const STATUS_STYLES = {
  pending: "border-[#1E3A8A]/40 bg-[#1E3A8A]/10 text-[#1E3A8A]",
  approved: "border-emerald-500/40 bg-emerald-50 text-emerald-700",
  declined: "border-red-500/40 bg-red-50 text-red-600",
};

export const RestaurantsView = ({ refreshKey, onAuthFail }) => {
  const [signups, setSignups] = useState(null);
  const [filter, setFilter] = useState("all");
  const [decliningId, setDecliningId] = useState(null);
  const [note, setNote] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/admin/restaurant-signups");
      if (res.ok) setSignups(await res.json());
      else if (res.status === 401) onAuthFail();
      else toast.error("Could not load restaurant sign-ups.");
    } catch {
      toast.error("Could not load restaurant sign-ups.");
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const sendReply = async (id) => {
    setReplySending(true);
    try {
      const reply = await postReply("signup", id, replyText);
      setSignups((prev) => prev.map((r) => (r.id === id ? { ...r, replies: [...(r.replies || []), reply] } : r)));
      setReplyingTo(null);
      setReplyText("");
      toast.success("Reply sent.", { description: "Your reply was emailed and saved to the thread." });
    } catch (err) {
      toast.error("Could not send reply.", { description: err.message });
    } finally {
      setReplySending(false);
    }
  };

  const reviewSignup = async (id, status, decisionNote = "") => {
    try {
      const res = await apiFetch(`/admin/restaurant-signups/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, note: decisionNote || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatDetail(body.detail));
      setSignups((prev) => prev.map((s) => (s.id === id ? { ...s, status, decision_note: decisionNote || null } : s)));
      setDecliningId(null);
      setNote("");
      const label = status === "approved" ? "Application approved." : status === "declined" ? "Application declined." : "Moved back to pending review.";
      if (body.email_sent === false) {
        toast.warning(label, { description: "The notification email to the applicant could not be sent." });
      } else if (body.email_sent === true) {
        toast.success(label, { description: "The applicant has been notified by email." });
      } else {
        toast.success(label);
      }
    } catch (err) {
      toast.error("Could not update status.", { description: err.message });
    }
  };

  return (
    <div className="mt-12" data-testid="admin-signups-list">
      {signups === null ? (
        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-[#1E3A8A]" /></div>
      ) : signups.length === 0 ? (
        <div className="card-dark flex flex-col items-center p-16 text-center hover:transform-none" data-testid="admin-signups-empty">
          <Inbox size={28} className="text-[#1E3A8A]/60" />
          <p className="mt-4 font-sans text-xl text-[#0F172A]">No restaurant sign-ups yet</p>
          <p className="mt-2 text-sm text-[#94A3B8]">New restaurant onboarding applications will appear here.</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2" data-testid="signup-filters">
            {["all", "pending", "approved", "declined"].map((f) => {
              const n = f === "all" ? signups.length : signups.filter((s) => (s.status || "pending") === f).length;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`signup-filter-${f}`}
                  className={`rounded-full border px-4 py-2 font-sans text-[10px] tracking-[0.2em] transition-colors duration-300 ${
                    filter === f ? "border-[#1E3A8A]/60 bg-[#1E3A8A]/10 text-[#1E3A8A]" : "border-[#E2E8F0] text-[#94A3B8] hover:border-[#1E3A8A]/30 hover:text-[#1E3A8A]/75"
                  }`}
                >
                  {STATUS_LABELS[f] || "ALL"} · {n}
                </button>
              );
            })}
          </div>
          {signups.filter((s) => filter === "all" || (s.status || "pending") === filter).map((s, i) => (
            <article key={s.id || i} data-testid={`signup-card-${i}`} className="card-dark p-6 sm:p-8 hover:transform-none">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-[#1E3A8A]/30 px-3 py-1 font-sans text-[10px] tracking-[0.2em] text-[#1E3A8A]/80">
                    {(s.cuisine || "RESTAURANT").toUpperCase()}
                  </span>
                  <span data-testid={`signup-status-${i}`} className={`rounded-full border px-3 py-1 font-sans text-[10px] tracking-[0.2em] ${STATUS_STYLES[s.status || "pending"]}`}>
                    {STATUS_LABELS[s.status || "pending"]}
                  </span>
                </div>
                <time className="font-sans text-[11px] text-[#94A3B8]">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</time>
              </div>
              <div className="mt-4 flex flex-wrap items-baseline gap-x-3">
                <h3 className="font-sans text-lg text-[#0F172A]">{s.restaurant_name}</h3>
                <span className="text-sm text-[#94A3B8]">{s.contact_name}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                <a href={`mailto:${s.email}`} data-testid={`signup-email-${i}`} className="inline-flex items-center gap-1.5 text-[#1E3A8A]/80 transition-colors hover:text-[#1E3A8A]">
                  <Mail size={13} /> {s.email}
                </a>
                <a href={`tel:${(s.phone || "").replace(/\s+/g, "")}`} data-testid={`signup-phone-${i}`} className="inline-flex items-center gap-1.5 text-[#1E3A8A]/80 transition-colors hover:text-[#1E3A8A]">
                  <Phone size={13} /> {s.phone}
                </a>
                <span className="inline-flex items-center gap-1.5 text-[#64748B]"><MapPin size={13} /> {s.location}</span>
              </div>
              <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-[#64748B]">{s.description}</p>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 border-t border-[#E2E8F0] pt-4 text-sm sm:grid-cols-2">
                {s.menu && (
                  <div>
                    <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">MENU / SAMPLE DISHES</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-[#64748B]">{s.menu}</dd>
                  </div>
                )}
                {s.hours && (
                  <div>
                    <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">OPERATING HOURS</dt>
                    <dd className="mt-1 text-[#64748B]">{s.hours}</dd>
                  </div>
                )}
                <div>
                  <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">FULFILMENT</dt>
                  <dd className="mt-1 text-[#64748B]">{FULFILMENT_LABELS[s.fulfilment] || s.fulfilment}</dd>
                </div>
                {s.social && (
                  <div>
                    <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">SOCIAL / WEBSITE</dt>
                    <dd className="mt-1 text-[#64748B]">{s.social}</dd>
                  </div>
                )}
              </dl>
              {s.status === "declined" && s.decision_note && (
                <p data-testid={`signup-note-${i}`} className="mt-4 rounded-lg border border-red-500/25 bg-red-50 px-4 py-3 text-sm text-red-700">
                  Decline reason: {s.decision_note}
                </p>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-3">
                {(s.status || "pending") !== "approved" && (
                  <button onClick={() => reviewSignup(s.id, "approved")} data-testid={`signup-approve-${i}`} className="btn-ghost !border-emerald-500/40 !px-5 !py-2 !text-xs !text-emerald-700 hover:!border-emerald-400">
                    Approve
                  </button>
                )}
                {(s.status || "pending") !== "declined" && (
                  <button onClick={() => { setDecliningId(decliningId === s.id ? null : s.id); setNote(""); }} data-testid={`signup-decline-${i}`} className="btn-ghost !border-red-500/40 !px-5 !py-2 !text-xs !text-red-600 hover:!border-red-400">
                    Decline
                  </button>
                )}
                {(s.status || "pending") !== "pending" && (
                  <button onClick={() => reviewSignup(s.id, "pending")} data-testid={`signup-pending-${i}`} className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8] underline-offset-4 transition-colors hover:text-[#475569] hover:underline">
                    BACK TO PENDING
                  </button>
                )}
              </div>
              {decliningId === s.id && (
                <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-[#F2F4F7] p-4">
                  <label className="field-label" htmlFor={`decline-note-${i}`}>Reason for declining <span className="text-[#1E3A8A]">*</span></label>
                  <textarea
                    id={`decline-note-${i}`}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid={`decline-note-input-${i}`}
                    className="field resize-none"
                    placeholder="e.g. Incomplete details, outside launch area..."
                  />
                  <div className="mt-3 flex gap-3">
                    <button onClick={() => reviewSignup(s.id, "declined", note)} disabled={!note.trim()} data-testid={`decline-confirm-${i}`} className="btn-gold !px-5 !py-2 !text-xs disabled:cursor-not-allowed disabled:opacity-50">
                      Confirm Decline
                    </button>
                    <button onClick={() => setDecliningId(null)} data-testid={`decline-cancel-${i}`} className="btn-ghost !px-5 !py-2 !text-xs">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <ReplyHistory replies={s.replies} testId={`signup-${i}`} />
              <div className="mt-4">
                {replyingTo === s.id ? (
                  <ReplyBox
                    testId={`signup-${i}`}
                    sending={replySending}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onSend={() => sendReply(s.id)}
                    onCancel={() => { setReplyingTo(null); setReplyText(""); }}
                  />
                ) : (
                  <button onClick={() => { setReplyingTo(s.id); setReplyText(""); }} data-testid={`reply-open-signup-${i}`} className="btn-ghost !px-5 !py-2 !text-xs">
                    <Send size={13} /> Reply
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
